import { StatusUpdateState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { StatusUpdate } from '@tryiris-ai/common.protojs/agent/v1/agent';

/**
 * Derived firmware-update flags from a raw StatusUpdate message.
 *
 * Extracted from `<FirmwareStatus>` so the state-machine logic can be
 * unit-tested without standing up a React renderer. Per the
 * `ai-code-requires-tests` rule (global) — the pure reducer is what matters;
 * the JSX that consumes it is trivial.
 */
export interface UpdateStatus {
  /** Any non-idle update lifecycle state — controls whether we hide "Up to date". */
  updateAvailable: boolean;
  /** True only at the initial "update exists but download hasn't started" state — gates the "Update Firmware" CTA. */
  canStartDownload: boolean;
  isDownloading: boolean;
  isReadyToInstall: boolean;
  isInstalling: boolean;
  bytesDownloaded: number;
  bytesTotal: number;
}

/** Reduce a StatusUpdate proto into the boolean flags the UI renders from. */
export const deriveUpdateStatus = (stateUpdateStatus: StatusUpdate | undefined): UpdateStatus => {
  const state = stateUpdateStatus?.state ?? StatusUpdateState.UPDATE_STATE_IDLE;
  return {
    // "updateAvailable" is the union of all three active-update states so the
    // "Up to date" label only appears when the device is genuinely idle.
    updateAvailable: [
      StatusUpdateState.UPDATE_STATE_UPDATE_AVAILABLE,
      StatusUpdateState.UPDATE_STATE_DOWNLOADING,
      StatusUpdateState.UPDATE_STATE_WAITING_TO_INSTALL
    ].includes(state),
    canStartDownload: state === StatusUpdateState.UPDATE_STATE_UPDATE_AVAILABLE,
    isDownloading: state === StatusUpdateState.UPDATE_STATE_DOWNLOADING,
    isReadyToInstall: state === StatusUpdateState.UPDATE_STATE_WAITING_TO_INSTALL,
    isInstalling: state === StatusUpdateState.UPDATE_STATE_INSTALLING,
    bytesDownloaded: stateUpdateStatus?.bytesDownloaded ?? 0,
    bytesTotal: stateUpdateStatus?.bytesTotal ?? 0
  };
};

/**
 * Compute an integer 0-100 progress percentage for a firmware download.
 *
 * Guards against division-by-zero when bytesTotal hasn't been reported yet
 * (returns 0 instead of NaN/Infinity).
 */
export const computeDownloadPercent = (bytesDownloaded: number, bytesTotal: number): number => {
  if (bytesTotal <= 0) {
    return 0;
  }
  return Math.round((bytesDownloaded / bytesTotal) * 100);
};
